/**
 * Translation Statistics Utility
 * Calculates translation completion percentages from JSON files
 */

export interface LanguageStats {
  code: string;
  name: string;
  totalKeys: number;
  translatedKeys: number;
  percentage: number;
  namespaces: {
    [namespace: string]: {
      total: number;
      translated: number;
      percentage: number;
    };
  };
}

export interface TranslationStats {
  overallPercentage: number;
  languages: LanguageStats[];
  totalKeys: number;
}

/**
 * Count all keys in a JSON object (nested)
 */
const countKeys = (obj: any, prefix = ''): number => {
  let count = 0;
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const fullKey = prefix ? `${prefix}.${key}` : key;
      if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
        count += countKeys(obj[key], fullKey);
      } else {
        count++;
      }
    }
  }
  return count;
};

/**
 * Count translated keys (non-empty values) in a JSON object
 * A key is considered translated if it has a non-empty string value
 */
const countTranslatedKeys = (obj: any, prefix = ''): number => {
  let count = 0;
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const fullKey = prefix ? `${prefix}.${key}` : key;
      if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
        count += countTranslatedKeys(obj[key], fullKey);
      } else {
        const value = obj[key];
        // Consider a key translated ONLY if it has a non-empty string value
        // Empty strings, null, undefined are NOT considered translated
        if (typeof value === 'string' && value.trim().length > 0) {
          count++;
        }
        // Non-string values (numbers, booleans) are also considered translated
        else if (typeof value === 'number' || typeof value === 'boolean') {
          count++;
        }
        // null, undefined, empty strings are NOT translated
      }
    }
  }
  return count;
};

/**
 * Get translation statistics for all languages
 */
export const getTranslationStats = async (): Promise<TranslationStats> => {
  const languages = [
    { code: 'en', name: 'English' },
    { code: 'de', name: 'Deutsch' },
    { code: 'es', name: 'Español' },
    { code: 'fr', name: 'Français' },
    { code: 'fi', name: 'Suomi' },
    { code: 'tr', name: 'Türkçe' },
  ];

  const namespaces = ['common', 'login', 'forgotPassword'];
  
  // Load English as baseline (100% complete)
  const enCommon = await import('../locales/en/common.json');
  const enLogin = await import('../locales/en/login.json');
  const enForgotPassword = await import('../locales/en/forgotPassword.json');
  
  const baselineKeys = {
    common: countKeys(enCommon.default),
    login: countKeys(enLogin.default),
    forgotPassword: countKeys(enForgotPassword.default),
  };
  
  const totalKeys = Object.values(baselineKeys).reduce((sum, count) => sum + count, 0);
  
  const languageStats: LanguageStats[] = [];
  
  for (const lang of languages) {
    const namespaceStats: { [key: string]: { total: number; translated: number; percentage: number } } = {};
    let totalTranslated = 0;
    let totalForLang = 0;
    
    for (const ns of namespaces) {
      try {
        const translations = await import(`../locales/${lang.code}/${ns}.json`);
        const total = baselineKeys[ns as keyof typeof baselineKeys];
        const translated = countTranslatedKeys(translations.default);
        
        namespaceStats[ns] = {
          total,
          translated,
          percentage: total > 0 ? Math.round((translated / total) * 100) : 0,
        };
        
        totalTranslated += translated;
        totalForLang += total;
      } catch (error) {
        // Namespace doesn't exist for this language
        namespaceStats[ns] = {
          total: baselineKeys[ns as keyof typeof baselineKeys],
          translated: 0,
          percentage: 0,
        };
        totalForLang += baselineKeys[ns as keyof typeof baselineKeys];
      }
    }
    
    languageStats.push({
      code: lang.code,
      name: lang.name,
      totalKeys: totalForLang,
      translatedKeys: totalTranslated,
      percentage: totalForLang > 0 ? Math.round((totalTranslated / totalForLang) * 100) : 0,
      namespaces: namespaceStats,
    });
  }
  
  // Calculate overall percentage (average of all languages except English)
  const nonEnglishLanguages = languageStats.filter(l => l.code !== 'en');
  const overallPercentage = nonEnglishLanguages.length > 0
    ? Math.round(
        nonEnglishLanguages.reduce((sum, lang) => sum + lang.percentage, 0) / nonEnglishLanguages.length
      )
    : 0;
  
  return {
    overallPercentage,
    languages: languageStats,
    totalKeys,
  };
};
